import { useCallback } from 'react'
import { toast } from 'sonner'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'
import { triggerBrowserDownload } from '@/frontend/lib/download'

/** Minimal file shape needed to trigger a download. */
type DownloadableFile = { id: string; fileName: string }

/**
 * Provides a `downloadFile` callback that saves a single file straight to the
 * user's download folder via a presigned attachment URL.
 */
export function useFileDownloads() {
  const api = useOsApi()

  /** Fetches a presigned URL that forces an attachment download. */
  const fetchDownloadUrl = useCallback(
    async (fileId: string): Promise<string> => {
      const response = await api.get('/files/:fileId/download-url', {
        params: { fileId },
        query: { disposition: 'attachment' },
      })
      if (!response.success) throw new Error(response.error.message)
      return response.data.downloadUrl
    },
    [api]
  )

  const downloadFile = useCallback(
    async (file: DownloadableFile): Promise<void> => {
      try {
        const url = await fetchDownloadUrl(file.id)
        triggerBrowserDownload(url, file.fileName)
      } catch (error) {
        console.error('Failed to download file', file.id, error)
        toast.error('Failed to download file')
      }
    },
    [fetchDownloadUrl]
  )

  return { downloadFile }
}
